import type { PrimitiveColors, ColorScale } from '../colors/colors';
import type { radius, spacing, layout, border, BorderWidthToken as BorderWidthTokenImport } from '../sizes';
import type { TypographyConfig, FontToken, FontProperties } from '../typography/types';

/**
 * Border width token for theme border sizes
 * Re-exported from sizes module for theme API consistency
 */
export type BorderWidthToken = BorderWidthTokenImport;

/** Base color scale keys that can be used with dot notation (e.g., '50', '100', '500') */
export type ColorScaleKey = '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900' | '950';

/** All primitive color names from the colors object */
export type PrimitiveColorName = keyof PrimitiveColors;

/** Color scale names (colors that have shade variations like gray, blue, red) */
export type ColorScaleName = {
  [K in PrimitiveColorName]: PrimitiveColors[K] extends ColorScale ? K : never;
}[PrimitiveColorName];

/** Simple color names (colors that are single values like 'white', 'black', 'brandNeonGreen') */
export type SimpleColorName = {
  [K in PrimitiveColorName]: PrimitiveColors[K] extends string ? K : never;
}[PrimitiveColorName];

/** Color references with dot notation (e.g., 'gray.500', 'blue.300') */
export type ColorReference = `${ColorScaleName}.${ColorScaleKey}`;

/** All possible primitive color token values */
export type ColorToken = SimpleColorName | ColorReference | 'transparent';

/** Semantic background color tokens for surfaces and containers */
export type BackgroundColorTokens =
  | 'bg'
  | 'bg.base'
  | 'bg.sunken'
  | 'bg.raised'
  | 'bg.floating'
  | 'bg.inverted'
  | 'bg.panel'
  | 'bg.overlay'
  | 'bg.error'
  | 'bg.error.solid'
  | 'bg.warning'
  | 'bg.warning.solid'
  | 'bg.success'
  | 'bg.success.solid'
  | 'bg.info'
  | 'bg.info.solid'
  | 'bg.info.inverted'
  | 'bg.accent'
  | 'bg.accent.solid';

/** Semantic foreground color tokens for text and icons */
export type ForegroundColorTokens =
  | 'fg'
  | 'fg.constant'
  | 'fg.disabled'
  | 'fg.muted'
  | 'fg.muted.constant'
  | 'fg.inverted'
  | 'fg.inverted.constant'
  | 'fg.error'
  | 'fg.warning'
  | 'fg.success'
  | 'fg.accent';

/** Semantic border color tokens for outlines and dividers */
export type BorderColorTokens =
  | 'border'
  | 'border.subtle'
  | 'border.inverted'
  | 'border.error'
  | 'border.warning'
  | 'border.success'
  | 'border.info'
  | 'border.accent';

/** Semantic color tokens for card components and their states */
export type CardColorTokens =
  | 'card.bg.emphasized'
  | 'card.bg.emphasized.hover'
  | 'card.emphasized.stripes'
  | 'card.bg.emphasized.infobox'
  | 'card.bg.muted'
  | 'card.bg.muted.hover'
  | 'card.bg.delay'
  | 'card.bg.delay.hover'
  | 'card.active.stripes'
  | 'card.bg.ahead'
  | 'card.bg.ahead.hover'
  | 'card.bg.ontime'
  | 'card.bg.ontime.hover'
  | 'card.bg.subtle'
  | 'card.bg.subtle.hover';

/** Semantic color tokens for button variants and their states */
export type ButtonColorTokens =
  | 'button.bg.primary'
  | 'button.bg.primary.hover'
  | 'button.bg.primary.active'
  | 'button.bg.primary.subtle'
  | 'button.bg.primary.subtle.hover'
  | 'button.bg.primary.subtle.active'
  | 'button.bg.secondary'
  | 'button.bg.secondary.hover'
  | 'button.bg.secondary.active'
  | 'button.bg.ghost'
  | 'button.bg.ghost.hover'
  | 'button.bg.ghost.active'
  | 'button.bg.critical'
  | 'button.bg.critical.hover'
  | 'button.bg.critical.active'
  | 'button.bg.critical.subtle'
  | 'button.bg.critical.subtle.hover'
  | 'button.bg.critical.subtle.active'
  | 'button.bg.warning.subtle'
  | 'button.bg.warning.subtle.hover'
  | 'button.bg.warning.subtle.active'
  | 'button.bg.ai'
  | 'button.bg.ai.hover'
  | 'button.bg.ai.active';

/** Semantic color tokens for tag variants and their states */
export type TagColorTokens =
  | 'tag.secondary.bg.default'
  | 'tag.secondary.bg.hover'
  | 'tag.secondary.bg.active'
  | 'tag.secondary.solid.bg.default'
  | 'tag.secondary.solid.bg.hover'
  | 'tag.secondary.solid.bg.active'
  | 'tag.primary.bg.default'
  | 'tag.primary.bg.hover'
  | 'tag.primary.bg.active'
  | 'tag.primary.solid.bg.default'
  | 'tag.primary.solid.bg.hover'
  | 'tag.primary.solid.bg.active'
  | 'tag.warning.bg.default'
  | 'tag.warning.bg.hover'
  | 'tag.warning.bg.selected'
  | 'tag.warning.solid.bg.default'
  | 'tag.warning.solid.bg.hover'
  | 'tag.warning.solid.bg.active'
  | 'tag.critical.bg.default'
  | 'tag.critical.bg.hover'
  | 'tag.critical.bg.active'
  | 'tag.critical.solid.bg.default'
  | 'tag.critical.solid.bg.hover'
  | 'tag.critical.solid.bg.active'
  | 'tag.ai.bg.default'
  | 'tag.ai.bg.hover'
  | 'tag.ai.bg.active'
  | 'tag.ai.solid.bg.default'
  | 'tag.ai.solid.bg.hover'
  | 'tag.ai.solid.bg.active';

/** Semantic color tokens for input fields and their states */
export type InputColorTokens =
  | 'input.bg'
  | 'input.bg.hover'
  | 'input.bg.focus'
  | 'input.bg.disabled'
  | 'input.border'
  | 'input.border.hover'
  | 'input.border.focus'
  | 'input.border.error'
  | 'input.border.success'
  | 'input.text'
  | 'input.text.placeholder'
  | 'input.text.disabled';

/** Semantic color tokens for scrollbar components */
export type ScrollbarColorTokens = 'scrollbar.track' | 'scrollbar.thumb';

/** All semantic color tokens combined */
export type SemanticColorToken =
  | BackgroundColorTokens
  | ForegroundColorTokens
  | BorderColorTokens
  | CardColorTokens
  | ButtonColorTokens
  | TagColorTokens
  | InputColorTokens
  | ScrollbarColorTokens;

/** Combined color tokens (primitive + semantic) */
export type AllColorTokens = ColorToken | SemanticColorToken;

/** Dimension type for generic dimension token functions */
export type DimensionType = 'spacing' | 'radius' | 'layout';

/**
 * Border radius token keys
 * @todo Export enumerable types here and use them in size definitions, not vice versa
 */
export type RadiusToken = keyof typeof radius;

/** Spacing token keys for margins, padding, and gaps */
export type SpacingToken = keyof typeof spacing;

/** Negative spacing token for negative margins */
export type NegativeSpacingToken = `-${SpacingToken}`;

/** Button size variants */
export type ButtonSizeToken = 'lg' | 'md' | 'sm';

/** Modal size variants */
export type ModalSizeToken = 'xl' | 'lg' | 'md' | 'sm';

/** Chip size variants */
export type ChipSizeToken = 'lg' | 'md' | 'sm';

/** Chip visual variants */
export type ChipVariantToken = 'solid' | 'flat';

/** Chip color variants */
export type ChipColorToken = 'default' | 'primary' | 'Ahead/AI' | 'warning' | 'danger';

/** Size configuration containing all dimension tokens */
export interface SizeConfig {
  radius: typeof radius;
  spacing: typeof spacing;
  layout: typeof layout;
  border: typeof border;
}

/** Button type/variant tokens */
export type ButtonTypeToken =
  | 'primary'
  | 'primary.subtle'
  | 'secondary'
  | 'ghost'
  | 'critical'
  | 'critical.subtle'
  | 'warning.subtle'
  | 'ai';

/** Theme color values - can be color tokens or hex values */
export type ThemeColorValue = AllColorTokens | string;

/** Theme colors object with all semantic tokens */
export interface ThemeColors {
  [key: string]: ThemeColorValue;
}

/** Parsed theme colors where all references are resolved to hex values */
export interface ParsedThemeColors {
  [key: string]: string | number | Record<string, unknown>;
}

/** Theme definition structure (before parsing) */
export interface ThemeDefinition {
  typography: TypographyConfig;
  colors: ThemeColors;
  sizes: SizeConfig;
}

/** Complete theme structure (after parsing) with helper methods */
export interface Theme {
  typography: TypographyConfig;
  colors: ParsedThemeColors;
  sizes: SizeConfig;
  getColor: (color: AllColorTokens) => string;
  getFont: (fontToken: FontToken) => FontProperties;
  getSpacing: {
    (spacingToken: SpacingToken): string;
    (spacingToken: SpacingToken, withUnit: false): number;
  };
  getRadius: {
    (radiusToken: RadiusToken): string;
    (radiusToken: RadiusToken, withUnit: false): number;
  };
  getLayout: {
    <T extends string>(layoutProperty: string, token: T): string;
    <T extends string>(layoutProperty: string, token: T, withUnit: false): number;
  };
  getBorderWidth: {
    (borderToken: BorderWidthToken): string;
    (borderToken: BorderWidthToken, withUnit: false): number;
  };
}

/** Theme collection type mapping theme names to theme objects */
export interface ThemeCollection {
  [themeName: string]: Theme;
}

/**
 * Theme identifier
 * Currently supports light and dark, more themes may be added in the future
 */
export type ThemeId = 'light' | 'dark';

/** Default theme (light mode) */
export const DEFAULT_THEME: ThemeId = 'light';

/** Default dark theme */
export const DEFAULT_THEME_DARK: ThemeId = 'dark';
